import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { saveCroData } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Settings, Plus, X, Save } from "lucide-react";
import { toast } from "sonner";

const ALL_COUNTRIES = [["AR", "Argentina"], ["MX", "México"], ["CL", "Chile"], ["CO", "Colombia"], ["ES", "España"], ["UY", "Uruguay"], ["PE", "Perú"], ["BR", "Brasil"], ["US", "EE.UU."]];
const ALL_NETWORKS = ["Search", "Display", "Video", "Shopping", "Performance Max", "Facebook", "Instagram", "LinkedIn"];
const ALL_OBJECTIVES = ["Conversions", "Leads", "Awareness", "Traffic", "Engagement"];

export default function CroSettingsDialog({ clientId, croData, open, onOpenChange }) {
  const qc = useQueryClient();
  const c = croData?.config || {};
  const [form, setForm] = useState(emptyForm(c));

  useEffect(() => {
    if (open) setForm(emptyForm(croData?.config || {}));
  }, [open, croData]);

  const save = useMutation({
    mutationFn: () => saveCroData(clientId, { ...croData, config: form }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cro-data", clientId] });
      toast.success("Compromiso CRO guardado");
      onOpenChange(false);
    },
    onError: () => toast.error("No se pudo guardar"),
  });

  const toggle = (field, val) => {
    const arr = form[field] || [];
    setForm({ ...form, [field]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] });
  };

  const addCampaign = () => setForm({ ...form, active_campaigns: [...(form.active_campaigns || []), { name: "", platform: "Google Ads", objective: "Conversions", monthly_budget_usd: 0, status: "active" }] });
  const updateCampaign = (i, patch) => {
    const next = [...(form.active_campaigns || [])];
    next[i] = { ...next[i], ...patch };
    setForm({ ...form, active_campaigns: next });
  };
  const removeCampaign = (i) => setForm({ ...form, active_campaigns: form.active_campaigns.filter((_, idx) => idx !== i) });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="cro-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display"><Settings className="h-5 w-5 text-indigo-600" />Compromiso CRO</DialogTitle>
          <DialogDescription>Definí período, budget mensual, países, redes, objetivos y campañas comprometidas.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          <Section title="Período de reporte">
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Desde</Label><Input type="date" value={form.report_period_start} onChange={(e) => setForm({ ...form, report_period_start: e.target.value })} data-testid="cro-period-start" /></div>
              <div><Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Hasta</Label><Input type="date" value={form.report_period_end} onChange={(e) => setForm({ ...form, report_period_end: e.target.value })} data-testid="cro-period-end" /></div>
              <div><Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Budget mensual USD</Label><Input type="number" min="0" value={form.monthly_budget_usd} onChange={(e) => setForm({ ...form, monthly_budget_usd: Number(e.target.value) })} data-testid="cro-monthly-budget" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">GA4 Property ID (Analytics)</Label>
                <Input
                  value={form.ga4_property_id || ""}
                  onChange={(e) => setForm({ ...form, ga4_property_id: e.target.value })}
                  placeholder="ej. 123456789"
                  className="font-mono"
                  data-testid="cro-ga4-property-id"
                />
                <p className="text-[10px] text-slate-400 mt-1">Lo encontrás en GA4 → Admin → Property Settings.</p>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Google Ads Customer ID</Label>
                <Input
                  value={form.google_ads_customer_id || ""}
                  onChange={(e) => setForm({ ...form, google_ads_customer_id: e.target.value })}
                  placeholder="ej. 1005539297"
                  className="font-mono"
                  data-testid="cro-gads-customer-id"
                />
                <p className="text-[10px] text-slate-400 mt-1">10 dígitos sin guiones (esquina superior derecha en Google Ads).</p>
              </div>
            </div>
          </Section>

          <Section title="Países objetivo">
            <div className="flex flex-wrap gap-1.5">
              {ALL_COUNTRIES.map(([code, name]) => {
                const sel = (form.countries || []).includes(code);
                return <button key={code} type="button" onClick={() => toggle("countries", code)} className={`text-[11px] px-2 py-0.5 rounded-sm font-medium border ${sel ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`} data-testid={`cro-c-${code}`}>{code} <span className="opacity-70 text-[10px]">{name}</span></button>;
              })}
            </div>
          </Section>

          <Section title="Redes activas">
            <div className="flex flex-wrap gap-1.5">
              {ALL_NETWORKS.map((n) => {
                const sel = (form.networks || []).includes(n);
                return <button key={n} type="button" onClick={() => toggle("networks", n)} className={`text-[11px] px-2 py-0.5 rounded-sm font-medium border ${sel ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-600 border-slate-200"}`} data-testid={`cro-n-${n}`}>{n}</button>;
              })}
            </div>
          </Section>

          <Section title="Objetivos">
            <div className="flex flex-wrap gap-1.5">
              {ALL_OBJECTIVES.map((o) => {
                const sel = (form.objectives || []).includes(o);
                return <button key={o} type="button" onClick={() => toggle("objectives", o)} className={`text-[11px] px-2 py-0.5 rounded-sm font-medium border ${sel ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-200"}`} data-testid={`cro-o-${o}`}>{o}</button>;
              })}
            </div>
          </Section>

          <Section title="Budget por campaña" highlight>
            <div className="text-[10px] text-slate-500 mb-2 grid grid-cols-12 gap-2 px-3">
              <div className="col-span-4">Nombre</div><div className="col-span-2">Plataforma</div><div className="col-span-2">Objetivo</div><div className="col-span-2 text-right">Budget mes USD</div><div className="col-span-1">Estado</div>
            </div>
            <div className="space-y-2 mb-2">
              {(form.active_campaigns || []).map((cmp, i) => (
                <div key={i} className="bg-white border border-slate-200 rounded-md p-3 grid grid-cols-12 gap-2 items-center" data-testid={`cro-campaign-${i}`}>
                  <Input placeholder="Nombre campaña" value={cmp.name} onChange={(e) => updateCampaign(i, { name: e.target.value })} className="h-8 col-span-4 text-xs" />
                  <select value={cmp.platform} onChange={(e) => updateCampaign(i, { platform: e.target.value })} className="h-8 col-span-2 text-xs border border-slate-200 rounded-md px-2 bg-white">
                    <option>Google Ads</option><option>Meta Ads</option><option>LinkedIn Ads</option><option>TikTok Ads</option>
                  </select>
                  <select value={cmp.objective} onChange={(e) => updateCampaign(i, { objective: e.target.value })} className="h-8 col-span-2 text-xs border border-slate-200 rounded-md px-2 bg-white">
                    {ALL_OBJECTIVES.map((o) => <option key={o}>{o}</option>)}
                  </select>
                  <Input type="number" min="0" placeholder="Budget mes" value={cmp.monthly_budget_usd || 0} onChange={(e) => updateCampaign(i, { monthly_budget_usd: Number(e.target.value) })} className="h-8 col-span-2 text-xs text-right font-mono" title="Budget mensual USD" />
                  <select value={cmp.status} onChange={(e) => updateCampaign(i, { status: e.target.value })} className="h-8 col-span-1 text-xs border border-slate-200 rounded-md px-1 bg-white">
                    <option value="active">Activa</option><option value="paused">Pausada</option>
                  </select>
                  <button type="button" onClick={() => removeCampaign(i)} className="text-rose-500 hover:text-rose-700 col-span-1 flex justify-end" data-testid={`cro-rm-camp-${i}`}><X className="h-4 w-4" /></button>
                </div>
              ))}
              {(form.active_campaigns || []).length === 0 && <div className="text-xs text-slate-400 italic">Sin campañas cargadas.</div>}
            </div>
            {(form.active_campaigns || []).length > 0 && (() => {
              const sumBudget = (form.active_campaigns || []).reduce((acc, c) => acc + (Number(c.monthly_budget_usd) || 0), 0);
              const diff = (form.monthly_budget_usd || 0) - sumBudget;
              return (
                <div className="text-[11px] text-slate-600 px-3 mb-2 font-mono flex flex-wrap gap-x-4 gap-y-1">
                  <span>Σ campañas: <span className="font-semibold text-slate-900">${sumBudget.toLocaleString()}</span></span>
                  <span>Budget total: <span className="font-semibold text-slate-900">${(form.monthly_budget_usd || 0).toLocaleString()}</span></span>
                  <span className={diff === 0 ? "text-emerald-700" : diff > 0 ? "text-amber-700" : "text-rose-700"}>
                    {diff === 0 ? "✓ balanceado" : diff > 0 ? `Sin asignar: $${diff.toLocaleString()}` : `Excede en $${Math.abs(diff).toLocaleString()}`}
                  </span>
                </div>
              );
            })()}
            <Button type="button" variant="outline" size="sm" onClick={addCampaign} data-testid="cro-add-campaign"><Plus className="h-3.5 w-3.5 mr-1" /> Agregar campaña</Button>
          </Section>

          <Section title="Notas">
            <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Foco del cliente, audiencias, restricciones..." data-testid="cro-notes" />
          </Section>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="cro-save-btn" className="bg-slate-900 hover:bg-slate-800">
            <Save className="h-4 w-4 mr-1.5" />{save.isPending ? "Guardando..." : "Guardar compromiso"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function emptyForm(c) {
  return {
    report_period_start: c.report_period_start || new Date().toISOString().slice(0, 10),
    report_period_end: c.report_period_end || new Date().toISOString().slice(0, 10),
    monthly_budget_usd: c.monthly_budget_usd || 0,
    ga4_property_id: c.ga4_property_id || "",
    google_ads_customer_id: c.google_ads_customer_id || "",
    countries: c.countries || [],
    networks: c.networks || [],
    objectives: c.objectives || [],
    platforms: c.platforms || [],
    active_campaigns: c.active_campaigns || [],
    notes: c.notes || "",
  };
}

function Section({ title, highlight, children }) {
  return (
    <div className={`${highlight ? "bg-indigo-50/40 border-indigo-200" : "bg-slate-50 border-slate-200"} border rounded-md p-4`}>
      <h4 className={`text-[11px] uppercase tracking-wider font-semibold mb-3 ${highlight ? "text-indigo-700" : "text-slate-600"}`}>{title}</h4>
      {children}
    </div>
  );
}
